<template>
	<Sidebar :collapsible="isMobile ? 'offcanvas' : 'icon'">
		<SidebarHeader class="px-3 pt-8">
			<Logo class="h-6 w-auto md:pt-10 pb-3" @click="goToHome" />
		</SidebarHeader>

		<SidebarContent v-if="isRulesLoaded">
			<SidebarGroup class="mt-2 md:mt-3">
				<SidebarMenu class="space-y-2 md:space-y-2.5">
					<SidebarMenuItem>
						<RouterLink
							:to="`/cabinet/company/${selectedCompany}`"
							custom
							v-slot="{ href, navigate, isExactActive }"
						>
							<SidebarMenuButton
								:as="'a'"
								:href="href"
								:is-active="isExactActive"
								@click="navigate"
							>
								<svg width="16" height="16" viewBox="0 0 16 16">
									<rect width="6.4" height="6.4" rx="2" fill="currentColor" />
									<rect x="8.8" width="6.4" height="6.4" rx="2" fill="currentColor" />
									<rect x="8.8" y="9.6" width="6.4" height="6.4" rx="2" fill="currentColor" />
									<rect y="9.6" width="6.4" height="6.4" rx="2" fill="currentColor" />
								</svg>
								<span>{{ $t('nav.company_dashboard') }}</span>
							</SidebarMenuButton>
						</RouterLink>
					</SidebarMenuItem>

					<require-access data="analytics-list" action="read">
						<SidebarMenuItem>
							<RouterLink
								:to="`/cabinet/company/${selectedCompany}/analytics`"
								custom
								v-slot="{ href, navigate, isActive }"
							>
								<SidebarMenuButton
									:as="'a'"
									:href="href"
									:is-active="isActive"
									@click="navigate"
								>
									<svg width="18" height="18" viewBox="0 0 18 18">
										<path d="M7.976 1.658A7.976 7.976 0 1 0 15.951 9.634H7.976V1.658Z" fill="currentColor"/>
										<path d="M9.692 0V7.976H17.668A7.976 7.976 0 0 0 9.692 0Z" fill="currentColor"/>
									</svg>
									<span>{{ $t('nav.analytycs') }}</span>
								</SidebarMenuButton>
							</RouterLink>
						</SidebarMenuItem>
					</require-access>

					<require-access action="read" data="bills-list">
						<SidebarMenuItem>
							<RouterLink
								:to="`/cabinet/company/${selectedCompany}/invoices`"
								custom
								v-slot="{ href, navigate, isActive }"
							>
								<SidebarMenuButton
									:as="'a'"
									:href="href"
									:is-active="isActive"
									@click="navigate"
								>
									<svg width="13" height="17" viewBox="0 0 13 17">
										<path d="M12.133.107L10.833 1.594 8.992.107a.667.667 0 0 0-.65 0L6.5 1.594 4.658.107a.667.667 0 0 0-.65 0L2.167 1.594.867.107C.508-.155 0 .094 0 .532v15.936c0 .438.508.687.867.425L2.167 15.406 4.008 16.893c.188.139.45.139.65 0L6.5 15.406 8.342 16.893c.188.139.45.139.65 0l1.841-1.487 1.3 1.487c.356.262.867.013.867-.425V.532c0-.438-.508-.687-.867-.425Z" fill="currentColor"/>
									</svg>
									<span>{{ $t('nav.bills') }}</span>
								</SidebarMenuButton>
							</RouterLink>
						</SidebarMenuItem>
					</require-access>

					<require-access action="read" data="payout-to-card-list">
						<SidebarMenuItem>
							<RouterLink
								:to="`/cabinet/company/${selectedCompany}/payouts`"
								custom
								v-slot="{ href, navigate, isActive }"
							>
								<SidebarMenuButton
									:as="'a'"
									:href="href"
									:is-active="isActive"
									@click="navigate"
								>
									<svg width="20" height="17" viewBox="0 0 20 17">
										<path d="M10.761 10.775H6.726V1.346H4.035v9.43H0l4.674 4.663a1 1 0 0 0 1.413 0l4.674-4.664Z" fill="currentColor"/>
										<path d="M19.505 5.367H15.47v9.43h-2.69v-9.43H8.743l4.675-4.663a1 1 0 0 1 1.412 0l4.675 4.663Z" fill="currentColor"/>
									</svg>
									<span>{{ $t('nav.payouts') }}</span>
								</SidebarMenuButton>
							</RouterLink>
						</SidebarMenuItem>
					</require-access>

					<require-access data="settings-users-list" action="read">
						<SidebarMenuItem>
							<RouterLink
								:to="`/cabinet/company/${selectedCompany}/settings`"
								custom
								v-slot="{ href, navigate, isActive }"
							>
								<SidebarMenuButton
									:as="'a'"
									:href="href"
									:is-active="isActive"
									@click="navigate"
								>
									<svg width="18" height="18" viewBox="0 0 18 18">
										<path d="M17.809 7.487 15.62 7.26a6.9 6.9 0 0 0-.604-1.512l1.358-1.74a.42.42 0 0 0-.05-.53l-1.66-1.664a.42.42 0 0 0-.53-.05L12.45 3.176a6.9 6.9 0 0 0-1.51-.605L10.716.378A.42.42 0 0 0 10.263 0H7.924a.42.42 0 0 0-.377.302L7.32 2.496a6.9 6.9 0 0 0-1.51.605L4 1.815a.42.42 0 0 0-.53.05L1.811 3.48a.42.42 0 0 0 0 .53l1.358 1.74a6.9 6.9 0 0 0-.603 1.512L.377 7.487A.42.42 0 0 0 0 7.866v2.345c0 .233.151.384.302.384l2.188.227c.15.53.376 1.059.603 1.513l-1.358 1.74a.42.42 0 0 0 0 .53l1.66 1.664a.42.42 0 0 0 .53.05l1.736-1.361c.453.303.981.455 1.51.606l.226 2.193c0 .227.151.303.377.303h2.34c.226 0 .377-.151.377-.303l.226-2.193c.529-.151 1.057-.378 1.51-.606l1.736 1.361a.42.42 0 0 0 .53-.05l1.66-1.664a.42.42 0 0 0 0-.53l-1.358-1.74c.302-.454.453-.983.604-1.513l2.188-.227c.226 0 .302-.151.302-.384V7.866a.42.42 0 0 0-.075-.379ZM9.055 12.1A3.025 3.025 0 1 1 12.074 9.076 3.033 3.033 0 0 1 9.055 12.1Z" fill="currentColor"/>
									</svg>
									<span>{{ $t('nav.settings') }}</span>
								</SidebarMenuButton>
							</RouterLink>
						</SidebarMenuItem>
					</require-access>

					<require-access data="x-market" action="read">
						<SidebarMenuItem>
							<RouterLink
								:to="`/cabinet/company/${selectedCompany}/receipts`"
								custom
								v-slot="{ href, navigate, isActive }"
							>
								<SidebarMenuButton
									:as="'a'"
									:href="href"
									:is-active="isActive"
									@click="navigate"
								>
									<svg width="18" height="17" viewBox="0 0 18 17">
										<path d="M17.11 7.72 16.496 1.633A1.24 1.24 0 0 0 15.235.404L9.362.004A1.24 1.24 0 0 0 8.317.404L.415 8.09a1.4 1.4 0 0 0 0 1.968l6.488 6.488a1.4 1.4 0 0 0 1.937 0l7.902-7.687c.277-.308.4-.708.368-1.139ZM11.699 9.29c.462.246.646.83.37 1.261-.277.462-.861.585-1.291.277L9.64 10.058c-.123-.092-.307 0-.277.154l.093 1.384c.03.522-.37.984-.892.984s-.953-.461-.892-.984l.092-1.384c.001-.154-.153-.246-.276-.154l-1.138.769c-.43.308-1.045.155-1.291-.277-.277-.461-.092-1.045.37-1.261l1.292-.646a.222.222 0 0 0 0-.338l-1.292-.646c-.461-.246-.646-.83-.37-1.261.277-.462.861-.585 1.292-.277l1.137.769c.123.092.307 0 .277-.154l-.092-1.384c-.031-.522.369-.984.892-.984s.953.461.892.984l-.093 1.384c0 .154.154.246.277.154l1.138-.768c.43-.308 1.045-.155 1.291.277.277.462.092 1.046-.37 1.261l-1.63.83 1.63.83Zm2.153-5.165c-.584 0-1.045-.523-.953-1.138a.98.98 0 0 1 .768-.769c.615-.092 1.138.37 1.138.954 0 .522-.431.953-.953.953Z" fill="currentColor"/>
										<circle cx="8.594" cy="8.613" r="4.472" fill="currentColor"/>
									</svg>
									<span>{{ $t('nav.x-market') }}</span>
								</SidebarMenuButton>
							</RouterLink>
						</SidebarMenuItem>
					</require-access>

					<require-access data="faq" action="read">
						<SidebarMenuItem>
							<RouterLink
								:to="`/cabinet/company/${selectedCompany}/receipts`"
								custom
								v-slot="{ href, navigate, isActive }"
							>
								<SidebarMenuButton
									:as="'a'"
									:href="href"
									:is-active="isActive"
									@click="navigate"
								>
									<svg width="15" height="15" viewBox="0 0 15 15">
										<path d="M7.5 0a7.5 7.5 0 1 0 0 15A7.5 7.5 0 0 0 7.5 0Zm.22 11.425a.8.8 0 0 1-1.4-.618c0-.553.269-.829.807-.829.54 0 .807.353.807.807 0 .26-.07.466-.214.64Zm1.958-5.362a2.16 2.16 0 0 1-.366.613c-.164.194-.445.447-.844.758-.34.267-.568.489-.684.665-.098.148-.151.342-.166.576-.066.37-.495.368-.495.368H6.897s-.373-.058-.36-.351c0-.403.073-.738.22-1.006.147-.268.403-.544.77-.83.435-.344.716-.61.842-.8.126-.19.19-.417.19-.68 0-.307-.102-.544-.307-.709-.205-.165-.5-.247-.884-.247-.348 0-.67.049-.967.148a9 9 0 0 0-.488.181c-.365.141-.534-.146-.534-.146l-.143-.3s-.161-.366.197-.527A6.1 6.1 0 0 1 7.46 3.348c.717 0 1.287.176 1.708.527.421.352.632.837.632 1.456 0 .274-.04.519-.12.733Z" fill="currentColor"/>
									</svg>
									<span>{{ $t('nav.faq') }}</span>
								</SidebarMenuButton>
							</RouterLink>
						</SidebarMenuItem>
					</require-access>
				</SidebarMenu>
			</SidebarGroup>
		</SidebarContent>

		<SidebarFooter class="mt-auto mb-5 md:mb-6 py-2 space-y-2">
			<SidebarMenuItem>
				<SidebarMenuButton :as="'a'" @click="openApiPage">
					<svg width="14" height="14" viewBox="0 0 14 14">
						<path d="M3.5 5c0 .732.166 1.424.449 2.051L0 11v1.5C0 13.328.896 14 2 14h2v-2h2v-2c0 0 2.329 0 2.5 0A5 5 0 1 0 3.5 5Zm5 2A2 2 0 1 1 8.5 3a2 2 0 0 1 0 4Z" fill="currentColor"/>
					</svg>
					<span>{{ $t('nav.api') }}</span>
				</SidebarMenuButton>
			</SidebarMenuItem>

			<SidebarMenu class="space-y-2 md:space-y-2.5">
				<SidebarMenuItem>
					<RouterLink
						:to="`/cabinet/company/${selectedCompany}/support`"
						custom
						v-slot="{ href, navigate, isActive }"
					>
						<SidebarMenuButton
							:as="'a'"
							:href="href"
							:is-active="isActive"
							@click="navigate"
						>
							<svg width="18" height="18" viewBox="0 0 22 22">
								<path d="M5.67 17.188c-1.95 0-3.5-1.585-3.5-3.523 0-1.79 1.27-3.29 2.98-3.35h1.09v6.873H5.67Z" fill="currentColor"/>
								<path d="M16.33 17.188c1.95 0 3.5-1.585 3.5-3.523 0-1.79-1.27-3.29-2.98-3.35H15.12v6.873h1.21Z" fill="currentColor"/>
								<path d="M4.812 10.312v-.811c-.025-1.417.432-2.8 1.295-3.924A6.96 6.96 0 0 1 14.843 4.483c.734.582 1.327 1.323 1.733 2.167.406.845.615 1.77.612 2.707v.956" stroke="currentColor" stroke-width="2" fill="none"/>
							</svg>
							<span>{{ $t('nav.support') }}</span>
						</SidebarMenuButton>
					</RouterLink>
				</SidebarMenuItem>
			</SidebarMenu>

			<LocaleSwitcher v-if="isMobile" />
		</SidebarFooter>


		<SidebarRail />
	</Sidebar>
</template>

<script>
import Logo from '../../components/base/Logo.vue'
import responsiveMixin from '@vue-app/mixins/responsive-mixin'
import LocaleSwitcher from '@vue-app/components/header/parts/LocaleSwitcher.vue'
import RequireAccess from '@vue-app/components/base/role-based/RequireAccess.vue'
import { mapState } from 'vuex'
import {
	Sidebar,
	SidebarHeader,
	SidebarContent,
	SidebarFooter,
	SidebarRail,
	SidebarGroup,
	SidebarMenu,
	SidebarMenuItem,
	SidebarMenuButton,
} from '@ui/sidebar'

export default {
	name: 'CompanySidebar',
	components: {
		RequireAccess,
		LocaleSwitcher,
		Logo,
		Sidebar,
		SidebarHeader,
		SidebarContent,
		SidebarFooter,
		SidebarRail,
		SidebarGroup,
		SidebarMenu,
		SidebarMenuItem,
		SidebarMenuButton,
	},
	mixins: [responsiveMixin],
	computed: {
		...mapState(['selectedCompany', 'isRulesLoaded']),
	},
	methods: {
		goToHome() { this.$router.push('/cabinet') },
		openApiPage() {
			window.open('https://hackmd.io/Mj2ZqCVpSq6A6Jzm6ladlw?view', '_blank')
		},
	},
}
</script>

<style scoped>
[data-slot="sidebar"][data-state="collapsed"] :deep(.logo__text) {
	opacity: 0;
	width: 0;
	pointer-events: none;
}
[data-slot="sidebar"][data-state="expanded"] :deep(.logo__text) {
	opacity: 1;
	width: auto;
}
[data-slot="sidebar"][data-state="collapsed"] :deep(.logo__icon svg) {
	max-width: 23px;
	height: auto;
}
</style>
